import * as signalR from "@microsoft/signalr";
import { getStoredAuthToken, isTokenExpired } from "./authToken";

const NOTIFICATION_SUCCESS_EVENT = "NotificationSuccess";
const NOTIFICATION_FAILED_EVENT = "NotificationFailed";

// How long (ms) to wait for connection + group join before giving up.
// The API call will still proceed — we just won't have SignalR coverage.
const SIGNALR_CONNECT_TIMEOUT_MS = 10000;

const getHubUrl = () => {
  if (import.meta.env.VITE_MERGE_PATIENTS_SIGNALR_HUB_URL) {
    return `${import.meta.env.VITE_MERGE_PATIENTS_SIGNALR_HUB_URL}/hubs/notification`;
  }
  return "";
};

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(
        () => reject(new Error(`SignalR connection timed out after ${ms}ms`)),
        ms,
      ),
    ),
  ]);

class NotificationsHub {
  constructor() {
    this.connection = null;
    this.startPromise = null;
    this.successListeners = new Set();
    this.failedListeners = new Set();
  }

  getAccessToken = () => {
    const token = getStoredAuthToken();
    if (!token || isTokenExpired(token)) return "";
    return token;
  };

  ensureConnection = () => {
    if (this.connection) return this.connection;

    const hubUrl = getHubUrl();
    if (!hubUrl) return null;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: this.getAccessToken,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on(NOTIFICATION_SUCCESS_EVENT, (payload) => {
      this.successListeners.forEach((listener) => listener(payload));
    });

    connection.on(NOTIFICATION_FAILED_EVENT, (payload) => {
      this.failedListeners.forEach((listener) =>
        listener(
          payload?.message || payload?.Message || "Notification failed.",
        ),
      );
    });

    this.connection = connection;
    return this.connection;
  };

  start = async () => {
    const connection = this.ensureConnection();
    if (!connection) return;

    if (
      connection.state === signalR.HubConnectionState.Connected ||
      connection.state === signalR.HubConnectionState.Connecting
    ) {
      return this.startPromise || Promise.resolve();
    }
    if (this.startPromise) return this.startPromise;

    this.startPromise = connection
      .start()
      .catch(() => {})
      .finally(() => {
        this.startPromise = null;
      });

    return this.startPromise;
  };

  stop = async () => {
    if (!this.connection) return;
    if (this.connection.state === signalR.HubConnectionState.Disconnected)
      return;

    try {
      await this.connection.stop();
    } catch {
      // no-op
    }
  };
  subscribeMergeResult = async (transactionId, onSuccess, onFailed) => {
    const successHandler = (payload) => onSuccess(payload);
    const failedHandler = (errorMessage) => onFailed(errorMessage);

    this.successListeners.add(successHandler);
    this.failedListeners.add(failedHandler);

    try {
      await withTimeout(this.start(), SIGNALR_CONNECT_TIMEOUT_MS);

      if (
        transactionId &&
        this.connection?.state === signalR.HubConnectionState.Connected
      ) {
        await withTimeout(
          this.connection.invoke("JoinGroup", String(transactionId)),
          SIGNALR_CONNECT_TIMEOUT_MS,
        );
      }
    } catch (err) {
      console.warn(
        "[NotificationsHub] Could not establish SignalR before API call:",
        err?.message,
      );
    }

    return async () => {
      try {
        if (
          transactionId &&
          this.connection?.state === signalR.HubConnectionState.Connected
        ) {
          await withTimeout(
            this.connection.invoke("LeaveGroup", String(transactionId)),
            SIGNALR_CONNECT_TIMEOUT_MS,
          );
        }
      } catch (err) {
        console.warn(
          "[NotificationsHub] Could not leave SignalR group:",
          err?.message,
        );
      }

      this.successListeners.delete(successHandler);
      this.failedListeners.delete(failedHandler);

      if (this.successListeners.size === 0 && this.failedListeners.size === 0) {
        this.stop();
      }
    };
  };
}

export const notificationsHub = new NotificationsHub();
